import ReportingFilter from "components/reportingFilter/ReportingFilter";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import cn from "classnames";
import {
  createDateRange,
  getFormattedTimeStringFromPeriod,
  noBarData,
  noData,
  validLineData,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import { type RootState } from "features/Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import { useAccountFilterShouldShow } from "features/Reporting/Content/queries/useAccountFilterShouldShow";
import { useChartPeriodMeasure, CHART_PERIOD } from "hooks/useChartPeriodMeasure";
import {
  sharedAccountReportingHorizontalBarProps,
  sharedAccountReportingLineProps,
} from "features/Reporting/Content/shared";
import { useQuery } from "@tanstack/react-query";
import {
  type BasePerformanceQueryContext,
  type PerformancePeriodQueryContext,
  type PaginationParams,
} from "features/Library/Common/models";
import { peopleReportingService } from "features/Reporting/services/peopleReportingService";
import { ChartWrapper, getDateFormatByCount, GroupedBarChart, HorizontalBarChart, LineChart } from "components/charts";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";

import CardReporting, { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import {
  GenericPerformanceListWithServerPagination,
  type ExportAvailability,
} from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type ColumnOption } from "interfaces/columnOptions";
import { Tooltip } from "components/common/tooltip";
import { TextTruncate } from "components";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";

import "features/Library/Common/utils/performanceSCSSUtils.scss";
import "../peopleStyles.scss";
import { sharedGoogleReportingBarProps } from "features/Reporting/Google/utils/shared";
import moment from "moment";
import { useGoogleTooltipFormats } from "features/Reporting/Google/utils/useGoogleTooltipFormats";
import {
  setHasDataToExport,
  setExportAction as setExportActionCreator,
} from "features/Reporting/state/export/exportSlice";
import { bindAction } from "interfaces";
import { bindActionCreators, type Dispatch } from "@reduxjs/toolkit";
import ReportingExport from "components/reportingExport/ReportingExport";
import { getPeopleAcquisitionDrilldownExport, getPeopleAcquisitionExport } from "../actions/peopleActions";
import { usePerformanceFilter } from "features/Library/Common/Hooks/usePerformanceFilter";
import { usePerformanceBreadcrumbs } from "features/Library/Common/Hooks/usePerformanceBreadcrumbs";
import { PeopleAcquisitionDrilldown } from "./PeopleAcquisitionDrilldown";
import { useReportingFormatWithTimeZone } from "hooks/useReportingFormatWithTimeZone";
import { fetchAcquisitionCampaignsPaginated, type AcquisitionCampaignRow } from "./queries";

const colorRange = [
  reportingColors.greenDark,
  reportingColors.blue,
  reportingColors.yellow,
  reportingColors.red,
  reportingColors.greyLight,
];
const colorRangeForSix = [
  reportingColors.greenDark,
  reportingColors.blue,
  reportingColors.yellow,
  reportingColors.red,
  reportingColors.purple,
  reportingColors.greyLight,
];

interface IncomingAcquisitionData {
  ClickDate: string;
  Clicks: number;
  CumulativeClicks: number;
  Medium: string;
}

type Props = {
  usesDropdownMenuNavigation?: boolean;
  renderHeader?: () => ReactNode;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
} & PropsFromRedux;

const lineChartFactoryAcquisition = (
  dataPoints: IncomingAcquisitionData[],
): { Date: Date[]; Clicks: number[][]; CumulativeClicks: number[][]; Mediums: string[] } => {
  if (dataPoints.length === 0) return { Date: [], Clicks: [], CumulativeClicks: [], Mediums: [] };

  const dataPointsWithUnspecified = dataPoints.map((dataPoint) => ({
    ...dataPoint,
    Medium: dataPoint.Medium || "Unspecified",
  }));

  // Group the data by date
  const groupedData: { [key: string]: IncomingAcquisitionData[] } = dataPointsWithUnspecified.reduce(
    (acc, dataPoint) => {
      const date = dataPoint.ClickDate;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(dataPoint);
      return acc;
    },
    {} as { [key: string]: IncomingAcquisitionData[] },
  );

  const Dates: Date[] = [];
  const Clicks: number[][] = [];
  const CumulativeClicks: number[][] = [];
  const mediumSet = new Set<string>();

  // Fill the Dates, Clicks, and CumulativeClicks arrays
  Object.entries(groupedData).forEach(([date, dataPointsWithUnspecified]) => {
    Dates.push(moment(date).local(true).toDate());
    const mediumMap: { [key: string]: { Clicks: number; CumulativeClicks: number } } = {};

    dataPointsWithUnspecified.forEach((dataPoint) => {
      mediumSet.add(dataPoint.Medium);
      if (!mediumMap[dataPoint.Medium]) {
        mediumMap[dataPoint.Medium] = { Clicks: 0, CumulativeClicks: 0 };
      }
      mediumMap[dataPoint.Medium].Clicks += dataPoint.Clicks;
      mediumMap[dataPoint.Medium].CumulativeClicks += dataPoint.CumulativeClicks;
    });

    const sortedMediums = Array.from(mediumSet).sort((a, b) => a.localeCompare(b));

    sortedMediums.forEach((medium) => {
      if (!mediumMap[medium]) {
        mediumMap[medium] = { Clicks: 0, CumulativeClicks: 0 };
      }
    });

    Clicks.push(sortedMediums.map((medium) => mediumMap[medium].Clicks));
    CumulativeClicks.push(sortedMediums.map((medium) => mediumMap[medium].CumulativeClicks));
  });

  // Transpose the Clicks and CumulativeClicks arrays
  const transposedClicks = Clicks[0].map((_, colIndex) => Clicks.map((row) => row[colIndex]));
  const transposedCumulativeClicks = CumulativeClicks[0].map((_, colIndex) =>
    CumulativeClicks.map((row) => row[colIndex]),
  );

  const Mediums = Array.from(mediumSet).sort((a, b) => a.localeCompare(b));

  // Create an array of objects containing Medium, Clicks, and CumulativeClicks
  const combined = Mediums.map((medium, index) => ({
    medium,
    clicks: transposedClicks[index],
    cumulativeClicks: transposedCumulativeClicks[index],
  }));

  // Sort the combined array based on the last value of the cumulativeClicks array.
  combined.sort(
    (a, b) => b.cumulativeClicks[b.cumulativeClicks.length - 1] - a.cumulativeClicks[a.cumulativeClicks.length - 1],
  );

  // Extract the sorted Mediums, Clicks, and CumulativeClicks
  const sortedMediums = combined.map((item) => item.medium);
  const sortedClicks = combined.map((item) => item.clicks);
  const sortedCumulativeClicks = combined.map((item) => item.cumulativeClicks);

  return { Date: Dates, Clicks: sortedClicks, CumulativeClicks: sortedCumulativeClicks, Mediums: sortedMediums };
};

const getLineChart = async ({ queryKey, signal }: PerformancePeriodQueryContext) => {
  const { data } = await peopleReportingService.acquisition.getLineChart(
    { ...queryKey[1], aggregation: queryKey[2] },
    signal!,
  );

  const factory = lineChartFactoryAcquisition(data);

  const barData = factory.Date.map((d, index) => {
    const mediums = factory.Mediums;
    const clicks = factory.Clicks;

    const result: { group: string; [key: string]: string | number } = {
      group: moment(d, "YYYY-MM-DD").format("YYYY-MM-DD"),
    };

    mediums.forEach((medium, mediumIndex) => {
      result[medium] = clicks[mediumIndex][index];
    });

    return result;
  });

  let dynamicColorRange: string[] = colorRangeForSix;

  if (factory.Mediums.length < 6) {
    const minimumRequiredLength = 1;
    dynamicColorRange = colorRange.slice(0, factory.Mediums.length ? factory.Mediums.length : minimumRequiredLength);
  }

  return {
    barProps: {
      ...sharedGoogleReportingBarProps,
      legendLabels: [...factory.Mediums],
      data: barData,
      order: factory.Mediums,
      colorRange: dynamicColorRange,
    },
    lineProps: {
      ...sharedAccountReportingLineProps,
      legendLabels: [...factory.Mediums],
      xData: Array.from({ length: factory.Clicks.length }).fill(factory.Date),
      clicks: factory.Clicks,
      cumulativeClicks: factory.CumulativeClicks,
      yData: [...factory.Clicks],
      xFormatterFunc: getDateFormatByCount(factory.Date.length),
      colorRange: dynamicColorRange,
    },
  };
};

const getEngagementChart = async ({ queryKey, signal }: BasePerformanceQueryContext) => {
  const { data } = await peopleReportingService.acquisition.getEngagementChart(queryKey[1], signal!);

  const dataWithUnspecified = data.map((dataPoint) => ({
    ...dataPoint,
    Medium: dataPoint.Medium || "Unspecified",
  }));

  dataWithUnspecified.sort((a, b) => b.Clicks - a.Clicks);

  const barData = dataWithUnspecified.map((item, index) => ({
    id: item.Medium.toLowerCase(),
    category: item.Medium,
    value: item.Clicks,
    fillColor: dataWithUnspecified.length === 6 ? colorRangeForSix[index] : colorRange[index],
  }));

  return barData;
};

const getEngagementCards = async ({ queryKey, signal }: BasePerformanceQueryContext): Promise<CardReportingItem[]> => {
  const { data } = await peopleReportingService.acquisition.getEngagementCards(queryKey[1], signal!);

  return [
    {
      statDescription: "Sources",
      popupBody:
        "Total number of campaign sources used to drive user acquisition across your account during the specified time period.",
      stat: data.DistinctSources,
    },
    {
      statDescription: "Mediums",
      popupBody:
        "Total number of mediums used to drive user acquisition across your account during the specified time period.",
      stat: data.DistinctMediums,
    },
    {
      statDescription: "Campaigns",
      popupBody:
        "Total number of campaigns launched to drive user acquisition across your account during the specified time period.",
      stat: data.DistinctCampaigns,
    },
    {
      statDescription: "Clicks",
      popupBody: "Total number of clicks from all campaigns across your account during the specified time period.",
      stat: data.Clicks,
    },
  ];
};

const TITLE = "Activity - Line Chart";
const TITLE_BAR = "Activity - Bar Chart";

export const AcquisitionReport = ({
  accountId,
  accountType,
  setHasExportData,
  setExportAction,
  getPeopleAcquisitionExport,
  getPeopleAcquisitionDrilldownExport,
  usesDropdownMenuNavigation,
  renderHeader,
  childAccountId,
  hasCreateAccountsAddOn,
}: Props) => {
  const isReportEnabled = useFeatureFlag(FeatureFlags.AcquisitionReport);
  const accountFilterShouldShow = useAccountFilterShouldShow(accountId, accountType);
  const [chartState, setChartState] = useState(TITLE);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [selectedMedium, setSelectedMedium] = useState("");
  const [selectedSource, setSelectedSource] = useState("");

  const initialOverrides = useMemo(() => {
    if (usesDropdownMenuNavigation && childAccountId) {
      return { accounts: [childAccountId] as number[], includeMyData: false };
    }
    return undefined;
  }, [usesDropdownMenuNavigation, childAccountId]);

  const [filter, setFilter] = usePerformanceFilter({
    includeAccountsDropdown: accountFilterShouldShow,
    supportsSPType: true,
    accountId,
    includeDistinct: true,
    initialOverrides,
  });
  const formatDateTime = useReportingFormatWithTimeZone();

  const { domElements, registerBreadcrumb, hasBreadcrumbsToDisplay } = usePerformanceBreadcrumbs();

  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);

  const handleCategorySelection = useCallback(
    (campaign: string, medium: string, source: string) => {
      registerBreadcrumb({
        text: campaign,
        action: () => {
          setSelectedCampaign("");
          setSelectedMedium("");
          setSelectedSource("");
        },
      });
      setSelectedCampaign(campaign);
      setSelectedMedium(medium);
      setSelectedSource(source);
    },
    [registerBreadcrumb],
  );

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
    chartState === TITLE_BAR,
  );

  const { groupedBarFormatter } = useGoogleTooltipFormats(chartPeriod, dateRange);

  const lineChartQuery = useQuery({
    queryFn: getLineChart,
    queryKey: ["acquisition line chart", filter, chartPeriod],
    enabled: chartPeriod !== CHART_PERIOD.UNSET && isReportEnabled && filter.type !== undefined,
  });

  const engagementChartQuery = useQuery({
    queryFn: getEngagementChart,
    queryKey: ["acquisition engagement chart", filter],
    enabled: isReportEnabled && filter.type !== undefined,
  });

  const engagementCardsQuery = useQuery({
    queryFn: getEngagementCards,
    queryKey: ["acquisition engagement cards", filter],
    enabled: isReportEnabled && filter.type !== undefined,
  });

  const campaignsQueryKey = useMemo(() => ["acquisition campaigns", filter] as const, [filter]);

  const fetchCampaignsPage = useCallback(
    (params: PaginationParams, signal?: AbortSignal) => fetchAcquisitionCampaignsPaginated(filter, params, signal),
    [filter],
  );

  const updateSummaryExportDataState = useCallback(
    ({ hasDataToExport }: ExportAvailability) => {
      setHasExportData({
        hasDataToExport,
        tooltipContent: "No data available for export. There are no mediums with interaction data.",
      });
    },
    [setHasExportData],
  );

  const lineWrapperProps = useMemo(
    () => ({
      titles: [TITLE, TITLE_BAR],
      showLegend: true,
      legendLabels: lineChartQuery.data?.lineProps.legendLabels ?? [],
      colorRange: lineChartQuery.data?.lineProps.colorRange ?? colorRange,
    }),
    [lineChartQuery],
  );

  const activitySection = useMemo(() => {
    if (!lineChartQuery.data) return null;

    if (chartState === TITLE) {
      return <LineChart {...lineChartQuery.data.lineProps} />;
    } else {
      return (
        <GroupedBarChart
          {...lineChartQuery.data.barProps}
          tooltipFormatter={groupedBarFormatter}
          xTickFormatter={(d) => getFormattedTimeStringFromPeriod(d.toString(), chartPeriod, dateRange)}
        />
      );
    }
  }, [lineChartQuery.data, chartState, groupedBarFormatter, chartPeriod, dateRange]);

  const columns = useMemo<ColumnOption<AcquisitionCampaignRow>[]>(
    () => [
      {
        name: "Source",
        width: 5,
        isSortable: true,
        sortKey: "Source",
        render(item) {
          return (
            <Tooltip target={<TextTruncate className="wordBreak">{item.Source}</TextTruncate>} content={item.Source} />
          );
        },
      },
      {
        render(item) {
          return (
            <Tooltip target={<TextTruncate className="wordBreak">{item.Medium}</TextTruncate>} content={item.Medium} />
          );
        },
        name: "Medium",
        isSortable: true,
        width: 4,
        sortKey: "Medium",
      },
      {
        width: 4,
        name: "Campaign",
        render(item) {
          return (
            <Tooltip
              target={
                <TextTruncate
                  className="invisibleButton"
                  onClick={() => handleCategorySelection(item.Campaign, item.Medium, item.Source)}
                >
                  {item.Campaign}
                </TextTruncate>
              }
              content={item.Campaign}
            />
          );
        },
        isSortable: true,
        sortKey: "Campaign",
      },
      {
        isSortable: true,
        width: 3,
        name: "Clicks",
        sortKey: "Clicks",
        render(item) {
          return <div>{item.Clicks}</div>;
        },
      },
      {
        name: "Last Activity",
        width: 4,
        isSortable: true,
        sortKey: "LastActivity",
        render(item) {
          return <div>{formatDateTime(item.LastActivity)}</div>;
        },
      },
    ],
    [handleCategorySelection, formatDateTime],
  );

  useEffect(() => {
    if (selectedCampaign) {
      setExportAction({
        method: getPeopleAcquisitionDrilldownExport,
        args: [filter, selectedCampaign, selectedMedium, selectedSource],
        isExportEnabled: true,
      });
    } else {
      setExportAction({
        method: getPeopleAcquisitionExport,
        args: [filter],
        isExportEnabled: true,
      });
    }
  }, [
    filter,
    setExportAction,
    getPeopleAcquisitionExport,
    getPeopleAcquisitionDrilldownExport,
    selectedCampaign,
    selectedMedium,
    selectedSource,
  ]);

  const ignoreInCountAndHide = useMemo(() => {
    const ignore = ["type"];
    if (usesDropdownMenuNavigation) {
      ignore.push("accounts", "includeMyData");
    }
    return ignore;
  }, [usesDropdownMenuNavigation]);

  if (!isReportEnabled) {
    return <ReportUnavailable />;
  }

  return (
    <div>
      <div className="performanceRoot">
        <div className={cn("performanceHeader", "peopleActivityHeader")}>
          {hasBreadcrumbsToDisplay ? (
            <div>{domElements}</div>
          ) : (
            <div className="performanceHeader peopleActivityFilters">
              {usesDropdownMenuNavigation && renderHeader ? (
                renderHeader()
              ) : (
                <h2 className="performanceTitle">Summary</h2>
              )}
              <div>
                {/* @ts-ignore */}
                <ReportingFilter
                  currentFilter={filter}
                  callback={setFilter}
                  includeAccountsDropdown={accountFilterShouldShow}
                  supportsSPType
                  ignoreInCountAndHide={ignoreInCountAndHide}
                  includeDistinct
                />
              </div>
            </div>
          )}
          <ReportingExport />
        </div>
        {selectedCampaign ? (
          <PeopleAcquisitionDrilldown
            accountId={accountId}
            filter={filter}
            selectedCampaign={selectedCampaign}
            selectedMedium={selectedMedium}
            selectedSource={selectedSource}
            childAccountId={childAccountId}
            hasCreateAccountsAddOn={hasCreateAccountsAddOn}
          />
        ) : (
          <div className="performanceBody">
            <div className="graphs">
              <div className="lineChartContainer" ref={measureRef}>
                <ChartWrapper {...lineWrapperProps} onChange={setChartState}>
                  <RequestStatusRenderer state={lineChartQuery.status}>
                    {lineChartQuery.isSuccess && validLineData(lineChartQuery.data.lineProps.yData)
                      ? activitySection
                      : noData(filter)}
                  </RequestStatusRenderer>
                </ChartWrapper>
              </div>
              <div className={cn("funnelChartContainer", "user_chart_container")}>
                <ChartWrapper titles={["Top Mediums"]}>
                  <RequestStatusRenderer state={engagementChartQuery.status}>
                    {engagementChartQuery.data && !noBarData(...engagementChartQuery.data.map((item) => item.value)) ? (
                      <HorizontalBarChart
                        {...sharedAccountReportingHorizontalBarProps}
                        data={engagementChartQuery.data}
                      />
                    ) : (
                      noData(filter)
                    )}
                  </RequestStatusRenderer>
                </ChartWrapper>
              </div>
            </div>
            <div className="performanceCardSection">
              <RequestStatusRenderer state={engagementCardsQuery.status}>
                {engagementCardsQuery.isSuccess && <CardReporting items={engagementCardsQuery.data} />}
              </RequestStatusRenderer>
            </div>
            <div className="table">
              <GenericPerformanceListWithServerPagination
                title="Medium Interactions"
                columns={columns}
                filter={filter}
                fetchPage={fetchCampaignsPage}
                queryKeyBase={campaignsQueryKey}
                hasDataForExport={updateSummaryExportDataState}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountId: state.userProfile.accountId,
    accountType: state.userProfile.accountTypeId,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    setHasExportData: bindAction(setHasDataToExport, dispatch),
    setExportAction: bindAction(setExportActionCreator, dispatch),
    getPeopleAcquisitionExport: bindActionCreators(getPeopleAcquisitionExport, dispatch),
    getPeopleAcquisitionDrilldownExport: bindActionCreators(getPeopleAcquisitionDrilldownExport, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(AcquisitionReport);
export default ConnectedComponent;
